<template>
  <DpaNewsGlobalLayout>
    <component :is="layoutComponent">
      <template v-if="showBanners" #banner>
        <DpaNewsBannerDisruptionInfo
          v-if="showDisruptionBanner"
          :message="disruptionBannerMessage" />
        <DpaNewsBannerMorningBriefing
          v-if="showMorningBriefingBanner"
          :url="layoutStore.morningBriefingBanner.url" />
        <RemainingTrialTime v-if="showRemainingTrialBanner" />
      </template>

      <RouterView />
    </component>

    <DpaNewsTermsOfServiceModal
      v-if="showTermsOfService"
      @abort="handleAbortTos"
      @submit="handleSubmitTos" />

    <DpaNewsWelcomeModal v-if="showWelcome" />

    <DpaNewsSnackbar
      :hide-automatically="layoutStore.snackbarMessages[0]?.autoHide"
      :icon="layoutStore.snackbarMessages[0]?.icon"
      :show-snackbar="layoutStore.snackbarMessages.length > 0"
      :type="layoutStore.snackbarMessages[0]?.type || 'success'"
      :action-label="layoutStore.snackbarMessages[0]?.actionLabel"
      :action-callback="layoutStore.snackbarMessages[0]?.actionCallback"
      @hide-snackbar="layoutStore.resetSnackbarMessages">
      {{ layoutStore.snackbarMessages[0]?.message }}
    </DpaNewsSnackbar>

    <Toaster />
  </DpaNewsGlobalLayout>
</template>

<script lang="ts" setup>
import { computed, provide, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";

import DpaNewsBannerDisruptionInfo from "@/components/shared/DpaNewsBanner/DpaNewsBannerDisruptionInfo.vue";
import DpaNewsBannerMorningBriefing from "@/components/shared/DpaNewsBanner/DpaNewsBannerMorningBriefing.vue";
import RemainingTrialTime from "@/components/shared/DpaNewsBanner/RemainingTrialTime.vue";
import DpaNewsSnackbar from "@/components/shared/DpaNewsSnackbar/DpaNewsSnackbar.vue";
import DpaNewsTermsOfServiceModal from "@/components/shared/DpaNewsTermsOfService/DpaNewsTermsOfServiceModal.vue";
import DpaNewsWelcomeModal from "@/components/shared/DpaNewsWelcomeModal/DpaNewsWelcomeModal.vue";
import Toaster from "@/components/shared/Toaster/Toaster.vue";
import { useAcceptTermsOfService } from "@/composition/api/signup.ts";
import useDisruptionInfoSocket from "@/composition/shared/sockets/useDisruptionInfoSocket.ts";
import useAuth from "@/composition/shared/useAuth.ts";
import useAuthorization from "@/composition/shared/useAuthorization.ts";
import {
  type LicenseType,
  useCustomDimensions,
} from "@/composition/shared/useCustomDimensionsMatomo.ts";
import useGlobalBanners from "@/composition/shared/useGlobalBanners.ts";
import useGlobalModals from "@/composition/shared/useGlobalModals.ts";
import { useTracking } from "@/composition/shared/useTracking.ts";
import DpaNewsGlobalLayout from "@/layouts/DpaNewsGlobalLayout.vue";
import layouts from "@/layouts.ts";
import { useLayoutStore } from "@/stores/layout.ts";
import { useMessengerSocketStore } from "@/stores/messengerSocketStore.ts";
import { useUserStore } from "@/stores/user.ts";
import { LayoutSymbol } from "@/symbols.ts";

import { useBookmarksStore } from "./stores/bookmarks.ts";

const { startUpDisruptionSocket } = useDisruptionInfoSocket();
const messengerSocketStore = useMessengerSocketStore();

const layoutStore = useLayoutStore();
const userStore = useUserStore();
const route = useRoute();

const {
  isPermittedForNewsfeed,
  isPermittedForDesk,
  isPermittedForCvd,
  isPermittedForMessenger,
  isPermittedForAiResearch,
  isPermittedForIntelligence,
} = useAuthorization();

const { execute: acceptTos } = useAcceptTermsOfService();
const { showTermsOfService, showWelcome } = useGlobalModals();
const {
  showBanners,
  showDisruptionBanner,
  disruptionBannerMessage,
  showMorningBriefingBanner,
  showRemainingTrialBanner,
} = useGlobalBanners();
const { logout, isAuthenticated, user } = useAuth();
const bookmarksStore = useBookmarksStore();

const {
  setLicenseType,
  isInternalGpNumber,
  isExternalGpNumber,
  setUserType,
  setLanguage,
  setInternalGpNumber,
  setExternalGpNumber,
  isValidLanguage,
  setSector,
} = useCustomDimensions();

const { trackEvent } = useTracking();

const layoutComponent = computed(
  () => layouts[route.meta.layout ?? "DefaultLayout"],
);

provide(LayoutSymbol, layoutComponent);

function hasMissingConsentCookie() {
  return !document.cookie.match(/(^|;)\s*OptanonAlertBoxClosed\s*=\s*([^;]+)/);
}

const userLicenseType = computed<LicenseType | null>(() => {
  if (!userStore.userPermissions) return null;

  if (
    !isPermittedForMessenger() &&
    !isPermittedForCvd() &&
    !isPermittedForDesk() &&
    !isPermittedForNewsfeed() &&
    isPermittedForIntelligence()
  ) {
    return "intelligence starter";
  }

  if (
    !isPermittedForMessenger() &&
    !isPermittedForDesk() &&
    isPermittedForCvd() &&
    isPermittedForNewsfeed() &&
    isPermittedForIntelligence()
  ) {
    return "intelligence basic";
  }

  if (
    !isPermittedForMessenger() &&
    isPermittedForCvd() &&
    isPermittedForDesk() &&
    isPermittedForNewsfeed() &&
    isPermittedForAiResearch() &&
    isPermittedForIntelligence()
  ) {
    return "intelligence premium";
  }

  if (
    isPermittedForNewsfeed() &&
    isPermittedForMessenger() &&
    isPermittedForCvd() &&
    isPermittedForDesk()
  ) {
    return "premium";
  }

  if (
    isPermittedForNewsfeed() &&
    !isPermittedForMessenger() &&
    isPermittedForCvd() &&
    isPermittedForDesk()
  ) {
    return "premium non-media";
  }

  if (
    !isPermittedForMessenger() &&
    isPermittedForCvd() &&
    isPermittedForNewsfeed() &&
    !isPermittedForDesk()
  ) {
    return "basic non-media";
  }

  if (
    !isPermittedForMessenger() &&
    !isPermittedForCvd() &&
    !isPermittedForDesk() &&
    isPermittedForNewsfeed()
  ) {
    return "basic";
  }

  return null;
});

const userAccessStatus = computed(() => {
  if (!userStore.userPermissions?.organization.gpNumber) return null;

  if (isInternalGpNumber(userStore.userPermissions?.organization.gpNumber))
    return "intern";
  if (isExternalGpNumber(userStore.userPermissions?.organization.gpNumber))
    return "extern";

  return "";
});

const handleAbortTos = async () => {
  await logout({
    logoutParams: {
      returnTo: window.location.origin,
    },
  });
};

const handleSubmitTos = async () => {
  await acceptTos();
};

watch(
  () => isAuthenticated.value,
  async (newIsAuthenticated: boolean) => {
    if (!newIsAuthenticated) return;

    await startUpDisruptionSocket();

    // Only show banner if consent cookie is missing
    if (hasMissingConsentCookie()) {
      setTimeout(() => {
        window.OneTrust?.ToggleInfoDisplay();
      }, 1500);
    }

    // This helps because OneTrust script activation for type="text/plain" may not re-run after login navigation.
    window.dispatchEvent(new Event("dpa:auth:login"));

    if (isPermittedForNewsfeed()) {
      await bookmarksStore.getBookmarkCount();
    }

    if (isPermittedForMessenger()) {
      await messengerSocketStore.startupMessengerSocket();
    }
  },
  { immediate: true },
);

function waitForAppcues(
  maxAttempts = 150,
  interval = 100,
): Promise<any | null> {
  return new Promise((resolve) => {
    let attempts = 0;
    function check() {
      if (window.Appcues && typeof window.Appcues.identify === "function") {
        resolve(window.Appcues);
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(check, interval);
      } else {
        resolve(null);
      }
    }
    check();
  });
}

watch(
  [
    () => user.value?.account_id,
    () => user.value?.sector,
    () => userStore.userPermissions,
    () => userStore.createDate,
    () => userStore.activationDate,
    () => userLicenseType.value,
    () => userAccessStatus.value,
  ],
  async ([
    id,
    sector,
    permissions,
    createDate,
    activationDate,
    license,
    accessStatus,
  ]) => {
    if (!userStore.createDate) return null;
    if (!userStore.activationDate) return null;

    if (
      id &&
      sector &&
      permissions &&
      createDate &&
      activationDate &&
      license &&
      accessStatus
    ) {
      const appcues = await waitForAppcues();
      if (!appcues) return;

      appcues.identify(id, {
        sector: sector,
        language: user.value?.lang,
        accessStatus: accessStatus,
        license: license,
        createDate: new Date(createDate).toString(),
        activationDate: new Date(activationDate).toString(),
      });

      if (typeof appcues.page === "function") {
        appcues.page();
      }
    }
  },
  { immediate: true },
);

/** send custom dimension values to Matomo */
watch(
  [
    () => userStore.userPermissions?.organization.gpNumber,
    () => userStore.preferences.preferredLanguage,
    () => user.value?.sector,
    userLicenseType,
  ],
  ([gpNumber, userLanguage, sector, licenseType]) => {
    if (!licenseType) return;
    setLicenseType(licenseType);

    if (gpNumber) {
      if (isInternalGpNumber(gpNumber)) {
        setUserType("intern");
        setInternalGpNumber(gpNumber);
      } else if (isExternalGpNumber(gpNumber)) {
        setUserType("extern");
        setExternalGpNumber(gpNumber);
      }
    }

    if (isValidLanguage(userLanguage)) {
      setLanguage(userLanguage);
    }
    setSector(sector);
  },
  { immediate: true },
);

const { t } = useI18n();
window.addEventListener("vite:preloadError", () => {
  layoutStore.addSnackbarMessage(
    t("snackbar.updateAvailable"),
    "success",
    "alert",
    false,
    t("generic.pageRefresh"),
    reloadPage,
  );
});

const reloadPage = () => {
  layoutStore.resetSnackbarMessages();
  trackEvent(
    "snackbar_click_snackbar_reloadToGetUpdate",
    t("generic.pageRefresh"),
  );
  window.location.reload();
};
</script>
